import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from './IconButton';
import { normalizeDialogWidth } from './dialogWidth';
import { useTabPane } from './tabPane';
import { InDialogContext } from './busyScope';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Mobilde tam ekran sheet, masaüstünde ortalanmış pencere. */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'wide' | 'full';
  /**
   * Yükseklik içerikle zıplamasın: bir taban verilir, taşan içerik kaydırılır.
   *
   * Saklı yordam penceresi gibi her açılışta farklı sayıda alan gösteren yerlerde
   * pencere boyu her seferinde değişiyordu; göz her açılışta yeniden yer arıyor.
   */
  steadyHeight?: boolean;
  dismissable?: boolean;
  /**
   * false: pencerenin dışına tıklamak kapatmaz (kapat düğmesi, Vazgeç ve Esc çalışır).
   * Emek isteyen formlar için — Excel kolon eşlemesi boşluğa yanlışlıkla tıklanınca kayboluyordu.
   */
  closeOnOutsideClick?: boolean;
  /**
   * İçerik kaydırmayı KENDİ yönetir: diyalog gövdesi taşmaz, belirli bir yüksekliğe
   * oturur ve çocuk bir sütun olarak onu doldurur.
   *
   * Bölmeli içerikler (kayıt formunun detay bölmesi) için şart: yüzde tabanlı bölücü
   * ölçecek bir yükseklik bulamazsa bölmeler sıfıra iner.
   */
  fill?: boolean;
  /** Başlık çubuğunda kapat düğmesinin SOLUNDA duran ek düğmeler (yönetici kısayolları). */
  titleExtra?: ReactNode;
  /**
   * Nesnenin EditFormWidth'i ("900px", "70vw", "min(800px, 90vw)"). Geçerliyse masaüstünde
   * `size` genişliğinin yerine geçer, ekrandan taşmaz (en fazla 95vw); geçersizse yok sayılır.
   * Telefonda diyalog yine alttan açılan tam genişlik tabakadır.
   */
  width?: string | null;
  /**
   * Verilirse kullanıcının kenardan/köşeden verdiği boyut bu anahtarla hatırlanır (tarayıcıda,
   * ör. `editform:<ObjectId>`). EditFormWidth varsayılandır, kullanıcı boyutu onun üstündedir.
   */
  persistKey?: string;
}

const sizes = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  // İki bölmeli içerikler (detaylı kayıt formu): iki yarım da çalışılabilir kalmalı.
  wide: 'sm:max-w-[min(90rem,94vw)]',
  full: 'sm:max-w-[95vw] sm:h-[92dvh]',
};

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  steadyHeight = false,
  dismissable = true,
  closeOnOutsideClick = true,
  titleExtra,
  fill = false,
  width,
  persistKey,
}: DialogProps) {
  const customWidth = size === 'full' ? null : normalizeDialogWidth(width);
  /**
   * Diyalog kapanınca klavye odağı AÇILMADAN önceki yere döner.
   *
   * Radix odağı "trigger"a geri verir; bu diyaloglar bir trigger'dan değil koddan
   * açılıyor (grid'de Enter, toolbar komutu), o yüzden odak body'ye düşüyordu:
   * kaydet/vazgeç sonrası ok tuşları ve kısayollar çalışmıyor, kullanıcı önce grid'e
   * tıklamak zorunda kalıyordu.
   */
  const openerRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (open) openerRef.current = document.activeElement as HTMLElement | null;
  }, [open]);

  /**
   * Pencere başlığından tutulup taşınabilir.
   *
   * Diyaloglar ekranın ortasına sabitti; altında kalan veriyi görmek için pencereyi
   * kapatmak gerekiyordu (bir hatayı okurken arkadaki satıra, bir formu doldururken
   * arkadaki listeye bakmak gibi).
   *
   * Taşıma yalnız masaüstünde: dar ekranda diyalog alttan açılan bir tabaka ve
   * sürüklemek düzeni bozardı. Konum diyalog her açıldığında sıfırlanır — bir önceki
   * kullanımdan kalan yer yeni içerik için doğru olmayabilir.
   */
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  /** Kullanıcının kenardan verdiği boyut (px); null = içerik/size'a göre. */
  const [userSize, setUserSize] = useState<{ w: number; h: number } | null>(null);
  const resizable = size !== 'full';
  const storageKey = persistKey ? `octo.dialogSize.${persistKey}` : null;

  useEffect(() => {
    if (!open) return;
    setOffset({ x: 0, y: 0 });
    setUserSize(storageKey && resizable ? readSize(storageKey) : null);
  }, [open, storageKey, resizable]);

  /**
   * Taşıma ve boyutlandırma (#460).
   *
   * Hareket sırasında konum/boyut DOĞRUDAN elemana yazılır, React durumu yalnız bırakınca
   * güncellenir: her fare hareketinde durum güncellemek bütün formu yeniden çiziyordu ve
   * pencere imleci takılarak izliyordu. Hareket boyunca sayfada metin seçimi kapalı —
   * yoksa imlecin geçtiği etiketler ve düğmeler seçiliyordu.
   */
  const gestureRef = useRef<{
    kind: 'move' | 'resize-x' | 'resize-y' | 'resize-xy';
    pointerId: number;
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
    baseW: number;
    baseH: number;
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);

  const beginGesture = useCallback(
    (kind: 'move' | 'resize-x' | 'resize-y' | 'resize-xy', e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0 || window.innerWidth < 640) return;
      // Kapat düğmesi ve başlıktaki diğer düğmeler sürüklemeyi başlatmamalı.
      if (kind === 'move' && (e.target as HTMLElement).closest('button')) return;
      const el = contentRef.current;
      if (!el) return;
      e.preventDefault();
      window.getSelection()?.removeAllRanges();
      const rect = el.getBoundingClientRect();
      gestureRef.current = {
        kind,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        baseX: offset.x,
        baseY: offset.y,
        baseW: rect.width,
        baseH: rect.height,
        x: offset.x,
        y: offset.y,
        w: rect.width,
        h: rect.height,
      };
      document.body.style.userSelect = 'none';
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [offset],
  );

  const moveGesture = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const g = gestureRef.current;
    const el = contentRef.current;
    if (!g || !el || g.pointerId !== e.pointerId) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (g.kind === 'move') {
      g.x = g.baseX + dx;
      g.y = g.baseY + dy;
    } else {
      // Pencere ortadan konumlu: kenar büyürken merkez yarı hızla kayar ki karşı kenar sabit kalsın.
      if (g.kind !== 'resize-y') {
        g.w = clamp(g.baseW + dx, MIN_W, window.innerWidth * 0.95);
        g.x = g.baseX + (g.w - g.baseW) / 2;
        el.style.width = `${g.w}px`;
        el.style.maxWidth = '95vw';
      }
      if (g.kind !== 'resize-x') {
        g.h = clamp(g.baseH + dy, MIN_H, window.innerHeight * 0.95);
        g.y = g.baseY + (g.h - g.baseH) / 2;
        el.style.height = `${g.h}px`;
      }
    }
    el.style.translate = `calc(-50% + ${g.x}px) calc(-50% + ${g.y}px)`;
  }, []);

  /**
   * Hareket bitince pencere ekranın içine çekilir (başlık çubuğu hep yakalanabilir kalsın)
   * ve sonuç duruma yazılır; boyut verildiyse hatırlanır.
   */
  const endGesture = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      const g = gestureRef.current;
      if (!g || g.pointerId !== e.pointerId) return;
      gestureRef.current = null;
      document.body.style.userSelect = '';
      const el = contentRef.current;
      const panel = el?.getBoundingClientRect();
      const bar = el?.querySelector('[data-dialog-titlebar]')?.getBoundingClientRect();
      let { x, y } = g;
      if (panel && bar) {
        const visible = 140;
        if (bar.top < 0) y += -bar.top;
        if (bar.top > window.innerHeight - bar.height) y -= bar.top - (window.innerHeight - bar.height);
        if (panel.right < visible) x += visible - panel.right;
        if (panel.left > window.innerWidth - visible) x -= panel.left - (window.innerWidth - visible);
      }
      setOffset({ x, y });
      if (g.kind !== 'move') {
        const next = { w: Math.round(g.w), h: Math.round(g.h) };
        setUserSize(next);
        if (storageKey) writeSize(storageKey, next);
      }
    },
    [storageKey],
  );

  /** Köşeye çift tık: kullanıcı boyutunu unut, varsayılana dön. */
  const resetSize = useCallback(() => {
    setUserSize(null);
    setOffset({ x: 0, y: 0 });
    if (storageKey) writeSize(storageKey, null);
    const el = contentRef.current;
    if (el) {
      el.style.width = '';
      el.style.height = '';
      el.style.maxWidth = '';
    }
  }, [storageKey]);

  const gestureHandlers = (kind: 'move' | 'resize-x' | 'resize-y' | 'resize-xy') => ({
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => beginGesture(kind, e),
    onPointerMove: moveGesture,
    onPointerUp: endGesture,
    onPointerCancel: endGesture,
  });

  /*
   * Pencere kendi sekmesi görünürken açılır. Pasif sekme mount kaldığı ve pencere body'ye
   * taşındığı için arkadaki sekmenin (ör. işi uzun süren SP'nin) penceresi öndeki sekmenin
   * üstüne çıkıp onu kilitliyordu. Sekmeye dönülünce açılır; bekleyen istek ve durum
   * pencereyi açan bileşende durduğu için kaybolmaz.
   */
  const paneVisible = useTabPane()?.visible ?? true;

  return (
    <RadixDialog.Root open={open && paneVisible} onOpenChange={dismissable ? onOpenChange : () => {}}>
      {/* İçerik pencerede olduğunu bilir: meşgul göstergesi pencerenin üstünde açılır (busyScope). */}
      <InDialogContext value={true}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="data-[state=open]:animate-in data-[state=open]:fade-in fixed inset-0 z-40 bg-[var(--overlay)]" />
        <RadixDialog.Content
          /*
           * Taşıma `translate` ile, `transform` ile DEĞİL.
           *
           * Ortalama sınıfı (`sm:-translate-x-1/2`) Tailwind 4'te `translate` CSS
           * özelliğini kuruyor. Inline `transform` vermek onu EZMİYOR, üstüne
           * ekleniyordu: iki kayma toplanıp pencere ilk tıklamada sol üste uçuyordu.
           * Aynı özelliği kullanınca sınıf düzgün geçersiz kılınıyor.
           */
          style={{
            ...(offset.x || offset.y
              ? { translate: `calc(-50% + ${offset.x}px) calc(-50% + ${offset.y}px)` }
              : null),
            ...(customWidth ? ({ '--dialog-w': customWidth } as CSSProperties) : null),
            ...(userSize && resizable ? { width: userSize.w, height: userSize.h, maxWidth: '95vw' } : null),
          }}
          ref={contentRef}
          /*
           * "Dışarı etkileşim" kararı ÖĞEYLE doğrulanır.
           *
           * Bölme ayırıcısı (react-resizable-panels) `pointerdown`'ı belge yakalama
           * evresinde kendi üstüne alıp odağı tutamaca veriyor; Radix'in kapatma
           * katmanı o basışı React ağacının dışında sayıp pencereyi kapatıyordu —
           * kullanıcı detay bölmesini genişletiyor, kayıt formu aniden yok oluyordu
           * (detaylı kayıtta ölçüldü). Olayın hedefi pencerenin içindeyse bu bir
           * dışarı tıklaması değildir; gerçek dışarı tıklama hâlâ kapatır.
           */
          onInteractOutside={(e) => {
            const target = e.target as Node | null;
            if (!closeOnOutsideClick || (target && contentRef.current?.contains(target))) e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            const opener = openerRef.current;
            // Öğe bu arada DOM'dan çıkmış olabilir (satır yenilendi, sekme kapandı).
            if (!opener || !document.contains(opener)) return;
            e.preventDefault();
            opener.focus({ preventScroll: true });
          }}
          className={cn(
            'bg-bg-elevated text-fg fixed z-50 flex flex-col shadow-[var(--shadow)] focus:outline-none',
            'inset-x-0 bottom-0 max-h-[95dvh] rounded-t-xl',
            'sm:inset-auto sm:top-1/2 sm:left-1/2 sm:w-full sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl',
            customWidth ? 'sm:w-[min(var(--dialog-w),95vw)] sm:max-w-[95vw]' : sizes[size],
            // Bölmeli gövde: pencere sabit yüksek açılır, içerik kendi içinde kaydırılır.
            fill && size !== 'full' && 'sm:h-[88dvh]',
            // Ölçülen dağılım: saklı yordamların yarısı 9 parametreye kadar; taban o
            // yoğunluğa göre seçildi, üstü zaten kaydırılıyor.
            steadyHeight && 'sm:min-h-[min(32rem,82dvh)]',
          )}
        >
          <div
            data-dialog-titlebar
            {...gestureHandlers('move')}
            className="border-border flex touch-none items-center gap-2 border-b px-4 py-3 select-none sm:[cursor:grab] sm:active:[cursor:grabbing]"
          >
            <RadixDialog.Title className="flex-1 truncate text-base font-semibold">{title}</RadixDialog.Title>
            {titleExtra}
            {dismissable ? (
              <RadixDialog.Close asChild>
                <IconButton label="Kapat" size="sm" tabIndex={-1}>
                  <X className="h-4 w-4" />
                </IconButton>
              </RadixDialog.Close>
            ) : null}
          </div>
          {description ? (
            <RadixDialog.Description className="text-fg-muted px-4 pt-3 text-sm">
              {description}
            </RadixDialog.Description>
          ) : null}
          {/* Yatay kaydırma KAPALI: bir satırın taşması bütün diyaloğu yana kaydırılabilir
              yapıyordu (ölçüldü: Ayarlar). Dikey kaydırma serbest. */}
          <div
            className={cn(
              'min-h-0 flex-1 px-4 py-3',
              fill ? 'flex flex-col overflow-hidden' : 'overflow-x-hidden overflow-y-auto',
            )}
          >
            {children}
          </div>
          {footer ? (
            <div
              className={cn(
                'border-border flex justify-end gap-2 border-t px-4 py-3',
                // Odaklı düğme GÖRÜNÜR olmalı: diyalog koddan açıldığında odak da koddan
                // veriliyor ve tarayıcı bunu ":focus-visible" saymıyor — kullanıcı Enter'ın
                // hangi düğmeye gideceğini göremiyordu.
                '[&_button:focus]:ring-ring [&_button:focus]:ring-2 [&_button:focus]:outline-none',
              )}
              onKeyDown={moveFocusWithArrows}
            >
              {footer}
            </div>
          ) : null}
          {/* Boyutlandırma tutamaçları (yalnız masaüstü): sağ kenar, alt kenar, köşe. */}
          {resizable ? (
            <>
              <div
                aria-hidden="true"
                className="absolute top-3 right-0 bottom-3 hidden w-1.5 cursor-ew-resize touch-none sm:block"
                {...gestureHandlers('resize-x')}
              />
              <div
                aria-hidden="true"
                className="absolute right-3 bottom-0 left-3 hidden h-1.5 cursor-ns-resize touch-none sm:block"
                {...gestureHandlers('resize-y')}
              />
              <div
                title={RESIZE_HINT}
                className="text-fg-subtle hover:text-fg-muted absolute right-0 bottom-0 hidden h-4 w-4 cursor-nwse-resize touch-none sm:block"
                onDoubleClick={resetSize}
                {...gestureHandlers('resize-xy')}
              >
                <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true">
                  <path
                    d="M14 6 6 14M14 10l-4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
      </InDialogContext>
    </RadixDialog.Root>
  );
}

const MIN_W = 360;
const MIN_H = 240;
const RESIZE_HINT = 'Boyutlandır — çift tık: varsayılan boyut';

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

/** Hatırlanan boyut; okunamazsa (gizli pencere, bozuk kayıt) yok sayılır. */
function readSize(key: string): { w: number; h: number } | null {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? 'null') as { w?: unknown; h?: unknown } | null;
    return v && typeof v.w === 'number' && typeof v.h === 'number' ? { w: v.w, h: v.h } : null;
  } catch {
    return null;
  }
}

function writeSize(key: string, size: { w: number; h: number } | null) {
  try {
    if (size) localStorage.setItem(key, JSON.stringify(size));
    else localStorage.removeItem(key);
  } catch {
    // depolama kapalı: boyut yalnız bu açılışta geçerli
  }
}

/**
 * Diyalog düğmeleri arasında ok tuşlarıyla gezinme. Yalnızca odak altbilgideyken
 * çalışır; formdaki metin kutularında ok tuşları imleci taşımaya devam eder.
 */
function moveFocusWithArrows(e: ReactKeyboardEvent<HTMLDivElement>) {
  const delta =
    e.key === 'ArrowLeft' || e.key === 'ArrowUp'
      ? -1
      : e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? 1
        : 0;
  if (delta === 0) return;
  const buttons = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
  if (buttons.length < 2) return;
  const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
  if (index < 0) return;
  e.preventDefault();
  buttons[(index + delta + buttons.length) % buttons.length]!.focus();
}
